import type { CSSProperties, ReactNode } from "react";
import { C, FM, FS, FU, T, TYPE, W } from "./tokens";

/* ────────────────────────────────────────────────────────────────────────────
   .ds-shell — the shared engine-shell chassis (design-system v2 §A0/§C).

   Hoists the token-override block that .gtd (gtm-distribution-section.tsx:166)
   and .gtmc-shell (gtm-connectivity-section.tsx:170) carry as byte-identical
   copies, plus the four additions the audit found missing (color-scheme, dark
   shadow tokens, region-series overrides, a light-flipping print pattern) and
   the §A1 retints that seat the shell on the home's navy ramp
   (navy.1 #0c1124 / navy.2 #080b16).

   The app root stays on the light theme; a DsShell is a self-contained dark
   island, exactly like the two GTM anchors. It must NEVER wrap the landing hub.
   The GTM anchors themselves migrate to this class in a follow-up (§E6) — do
   not edit their local blocks from here.
   ──────────────────────────────────────────────────────────────────────────── */

export function DsShell({
  accent = "#82BAFF",
  id,
  printRoot = false,
  style,
  children,
}: {
  /** Section identity pastel (§A1 accent.family) — drives the italic accent
   *  word + numerals via `--ds-accent`. Never interactive, never state-bearing. */
  accent?: string;
  id?: string;
  /** Marks this shell as the print root: printing reveals every mounted panel
   *  (including .ds-print-appendix blocks) on a light print palette. */
  printRoot?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className="ds-shell"
      data-ds-print-root={printRoot ? "" : undefined}
      style={{ "--ds-accent": accent, ...style } as CSSProperties}
    >
      {children}
      <style>{DS_SHELL_CSS}</style>
    </section>
  );
}

/* The shared dark-shell token override block (the hoisted 27-var set from
   gtd:166–192 ≡ gtmc:170–196, §A1-retinted, plus the audit's additions:
   color-scheme, dark shadow tokens, region-series, map stroke). Exported so a
   section that needs the same tokens WITHOUT DsShell's one-screen behaviors
   (rail hiding, frame removal) can interpolate it into its own scoped block —
   Intelligence 1.3 does this to keep its collapsible right rail. */
export const DS_SHELL_TOKENS = `
    --color-bg-primary: #080b16;
    --color-bg-secondary: rgba(255,255,255,0.025);
    --color-surface: rgba(255,255,255,0.045);
    --color-surface-alt: rgba(255,255,255,0.065);
    --color-tooltip-bg: #141a26;
    --color-border: rgba(255,255,255,0.10);
    --color-border-strong: rgba(255,255,255,0.22);
    --color-text-primary: #e9eefc;
    --color-text-secondary: #8d98b6;
    --color-text-muted: #5d6883;
    --color-text-on-accent: #04130d;
    --color-accent: #16c98d;
    --color-accent-hover: #24d99d;
    --color-fill-strong: #07101d;
    --color-on-fill-strong: #e9eefc;
    --color-series-arrivals: #82BAFF;
    --color-series-emerging: #16c98d;
    --color-series-crisis: #ff6b6b;
    --color-series-bar: #16c98d;
    --color-series-barneg: #ff6b6b;
    --color-series-purple: #b79cff;
    --color-overlay: rgba(255,255,255,0.055);
    --color-overlay-strong: rgba(255,255,255,0.12);
    /* The dark block had never declared this one, so a dark shell viewed while
       the app root was on LIGHT inherited :root's navy gridlines — invisible on
       a dark panel. Matches [data-theme="dark"] in tokens.css. */
    --color-overlay-grid: rgba(255,255,255,0.08);
    --color-highlight-soft: rgba(255,191,0,0.12);
    --color-amber-text: #ffbf00;
    --color-map-land-empty: rgba(255,255,255,0.10);
    --color-map-ocean: #080b16;
    --color-map-no-data: rgba(255,255,255,0.10);
    color-scheme: dark;
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.5);
    --shadow-md: 0 2px 8px rgba(0, 0, 0, 0.55);
    --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.6);
    --shadow-card-rest: 0 1px 3px rgba(0, 0, 0, 0.5), 0 8px 22px rgba(0, 0, 0, 0.55);
    --shadow-card-hover: 0 2px 8px rgba(0, 0, 0, 0.55), 0 16px 38px rgba(0, 0, 0, 0.65);
    --shadow-card-table: 0 1px 4px rgba(0, 0, 0, 0.5);
    --shadow-card-panel: 0 12px 34px rgba(0, 0, 0, 0.66);
    --color-region-mideast: #cb7e03;
    --color-region-asiapac: #26c5ad;
    --mmap-stroke: rgba(255,255,255,0.35);
    /* Shell-scoped semantic vars — the roles that used to be hardcoded
       rgba(255,255,255,…) literals inside each section's CSS. Tokenising them
       is what lets the light block below actually reach the section chrome. */
    --ds-shell-bg:
      radial-gradient(circle at 82% 2%, rgba(106,168,255,0.16), transparent 32%),
      radial-gradient(circle at 10% 20%, rgba(22,201,141,0.10), transparent 30%),
      linear-gradient(180deg, #0b1020 0%, #05060c 72%);
    --ds-shell-frame: rgba(255,255,255,0.10);
    --ds-shell-texture: rgba(255,255,255,0.06);
    --ds-kick: #82BAFF;
    --ds-hair: rgba(255,255,255,0.06);
    --ds-wash: rgba(255,255,255,0.03);
    --ds-sel-ring: rgba(255,255,255,0.65);
    --ds-scrim: rgba(6,7,13,0.92);
    --ds-option-bg: #0c1124;
    /* engine green, as a tinted fill / ring / CTA glow */
    --ds-engine-dim: rgba(22,201,141,0.12);
    --ds-engine-ring: rgba(22,201,141,0.30);
    --ds-engine-glow: 0 12px 40px -12px rgba(22,201,141,0.70);
    /* GTM section accent palette, as CHROME (badge text, chip fills, rules,
       borders). The byte-identical "P" object in each GTM section is the
       data-viz form of these same hues and stays literal per the tokens.ts
       policy - only the chrome usage flips. */
    --ds-p-green: #16c98d;
    --ds-p-green-dim: rgba(22,201,141,0.14);
    --ds-p-gold: #e9c46a;
    --ds-p-gold-dim: rgba(233,196,106,0.13);
    --ds-p-gold-line: rgba(233,196,106,0.45);
    --ds-p-blue: #6aa8ff;
    --ds-p-blue-soft: #9cc3ff;
    --ds-p-blue-deep: #3d6fb3;
    --ds-p-navy: #2c4f86;
    --ds-p-danger: #ff6b6b;
    --ds-p-danger-dim: rgba(255,107,107,0.12);
    --ds-p-warn: #ffb454;
    --ds-p-purple: #b79cff;
    /* frosted-card gradient stops (card.ts) — the glass tint that used to be a
       literal in the shared recipe, so every shell card flips with the theme. */
    --ds-card-a: rgba(255,255,255,0.055);
    --ds-card-b: rgba(255,255,255,0.025);
`;

/* The light counterpart. Same 40 custom properties, light values — hoisted
   from the print palette below (which had been the only light rendering of
   these shells) and extended to cover the series / shadow / semantic vars.

   Why a full second block rather than "just don't override on light": the
   shells declare roles the app root has no opinion on (--ds-kick, --ds-accent,
   the green engine accent, the shell background), so light needs explicit
   values for those regardless. Declaring the whole set keeps the two themes
   diffable side by side instead of half-inherited.

   The engine green (#16c98d) is the one hue that cannot survive the flip: it
   is 1.8:1 on white. Light uses #0d8a62 — same hue, dropped in lightness to
   clear AA as both text and a white-on-green fill. */
export const DS_SHELL_TOKENS_LIGHT = `
    --color-bg-primary: #ffffff;
    --color-bg-secondary: #f0f6ff;
    --color-surface: #ffffff;
    --color-surface-alt: #f0f6ff;
    --color-tooltip-bg: #ffffff;
    --color-border: #e0e7ef;
    --color-border-strong: #c3d4e8;
    --color-text-primary: #000f47;
    --color-text-secondary: #55617f;
    --color-text-muted: #8a93a8;
    --color-text-on-accent: #ffffff;
    --color-accent: #0d8a62;
    --color-accent-hover: #0a6f4e;
    --color-fill-strong: #000f47;
    --color-on-fill-strong: #ffffff;
    --color-series-arrivals: #0b4bff;
    --color-series-emerging: #0d8a62;
    --color-series-crisis: #c8272d;
    --color-series-bar: #5fa18f;
    --color-series-barneg: #c77c70;
    --color-series-purple: #5e017f;
    --color-overlay: rgba(0,15,71,0.05);
    --color-overlay-strong: rgba(0,15,71,0.12);
    --color-overlay-grid: rgba(0,15,71,0.06);
    --color-highlight-soft: #fff3da;
    --color-amber-text: #8a5200;
    --color-map-land-empty: #e4e6e3;
    --color-map-ocean: #ffffff;
    --color-map-no-data: #e4e6e3;
    color-scheme: light;
    --shadow-sm: 0 1px 2px rgba(0, 15, 71, 0.06);
    --shadow-md: 0 2px 8px rgba(0, 15, 71, 0.1);
    --shadow-lg: 0 8px 24px rgba(0, 15, 71, 0.14);
    --shadow-card-rest: 0 1px 3px rgba(0, 15, 71, 0.04), 0 4px 12px rgba(0, 15, 71, 0.06);
    --shadow-card-hover: 0 2px 6px rgba(0, 15, 71, 0.06), 0 8px 24px rgba(0, 15, 71, 0.1);
    --shadow-card-table: 0 1px 4px rgba(0, 15, 71, 0.06);
    --shadow-card-panel: 0 8px 24px rgba(0, 15, 71, 0.12);
    --color-region-mideast: #cb7e03;
    --color-region-asiapac: #0e7a6a;
    --mmap-stroke: rgba(0,15,71,0.45); /* navy: a white stroke is invisible on the white light-ocean (paired with --color-map-ocean:#fff above) */
    --ds-shell-bg:
      radial-gradient(circle at 82% 2%, rgba(11,75,255,0.05), transparent 32%),
      radial-gradient(circle at 10% 20%, rgba(13,138,98,0.05), transparent 30%),
      linear-gradient(180deg, #f7fafe 0%, #ffffff 72%);
    --ds-shell-frame: #e0e7ef;
    --ds-shell-texture: rgba(0,15,71,0.05);
    --ds-kick: #0b4bff;
    --ds-hair: rgba(0,15,71,0.07);
    --ds-wash: rgba(0,15,71,0.025);
    --ds-sel-ring: rgba(0,15,71,0.55);
    --ds-scrim: rgba(255,255,255,0.94);
    --ds-option-bg: #ffffff;
    --ds-engine-dim: rgba(13,138,98,0.10);
    --ds-engine-ring: rgba(13,138,98,0.35);
    --ds-engine-glow: 0 10px 28px -14px rgba(13,138,98,0.55);
    /* Same hues, dropped in lightness to clear AA on white. Gold is the one
       that has to move furthest — #e9c46a is 1.6:1 on white, so chrome gold
       becomes the brand's legible amber (#8a5200, == --color-amber-text). */
    --ds-p-green: #0d8a62;
    --ds-p-green-dim: rgba(13,138,98,0.10);
    --ds-p-gold: #8a5200;
    --ds-p-gold-dim: rgba(138,82,0,0.08);
    --ds-p-gold-line: rgba(138,82,0,0.35);
    --ds-p-blue: #0b4bff;
    --ds-p-blue-soft: #5b7fd6;
    --ds-p-blue-deep: #123a8f;
    --ds-p-navy: #000f47;
    --ds-p-danger: #c8272d;
    --ds-p-danger-dim: rgba(200,39,45,0.08);
    --ds-p-warn: #a35b00;
    --ds-p-purple: #5e017f;
    /* On light the glass has nothing to lift off, so the card carries a faint
       cool wash instead of a transparent tint — the border + shadow do the
       edge work that the white tint did on dark. Held just OFF pure white (in
       step with --color-surface) so cards recede under the warm-sunrise hero
       instead of out-shouting it (light-mode hero study, round 3). */
    --ds-card-a: #f7f9fc;
    --ds-card-b: #f1f5fb;
`;

/* Emits the paired token rules for a shell class. Call once per shell scope:
   the base block stays on the bare selector (so a shell renders dark even
   before the theme attribute is written), and the light block rides a
   `[data-theme="light"]` ancestor — (0,2,0) vs (0,1,0), so it wins without
   `!important`. `extraDark` / `extraLight` carry the per-shell deviations
   (e.g. Intelligence's deeper map ocean). */
export function shellThemeTokens(selector: string, extraDark = "", extraLight = ""): string {
  return `
  ${selector} { ${DS_SHELL_TOKENS} ${extraDark} }
  [data-theme="light"] ${selector} { ${DS_SHELL_TOKENS_LIGHT} ${extraLight} }
`;
}

const DS_SHELL_CSS = `
  /* ── one-screen sections don't scroll, so the "On this page" jump rail has
        nothing to jump to — the shell spans its 200px+32px column instead.
        Done with a negative margin rather than by retracking the grid: the
        wrapper transitions grid-template-columns, so a track change would
        animate on every section switch (decorative motion, prohibited) and
        leave other sections' rails mid-transition. The rail element itself is
        merely hidden while a shell is mounted (visibility has no transition),
        so light sections get it back untouched. Media-scoped to widths where
        SectionShell actually reserves the rail column. ── */
  @media (min-width: 1201px) {
    .trysec-grid[data-rail-mode] > .trysec-body > .ds-shell {
      margin-right: -232px;
      position: relative;
      z-index: 5;
    }
    .trysec-grid[data-rail-mode]:has(.ds-shell) .trysec-rail { visibility: hidden; }
  }

  /* ── the paired token blocks (§A0 additions included) — dark on the bare
        selector, light under [data-theme="light"]. See shellThemeTokens.
        .ds-audit gets its OWN call (not a comma-joined selector) because
        shellThemeTokens only prefixes [data-theme="light"] onto the FIRST
        selector — a comma list would leave .ds-audit on the light tokens
        unconditionally. The modal PORTALS to body (to escape the step-panel's
        transform), landing outside the shell, so it needs the same token set. ── */
  ${shellThemeTokens(".ds-shell")}
  ${shellThemeTokens(".ds-audit")}

  /* The identity pastel arrives as an INLINE --ds-accent, so no stylesheet rule
     can retint it for light — inline always wins. --ds-accent-ink is the
     derived text-safe form: itself on dark, mixed toward navy on light, where
     the pastels (#82BAFF et al) sit at ~2:1 on white. */
  .ds-shell, .ds-audit { --ds-accent-ink: var(--ds-accent); }
  [data-theme="light"] .ds-shell,
  [data-theme="light"] .ds-audit {
    --ds-accent-ink: color-mix(in oklab, var(--ds-accent) 55%, #000f47);
  }

  .ds-shell {
    /* ── section identity (overridden inline per section) ── */
    --ds-accent: #82BAFF;
    /* ── full-bleed (design-lead 2026-07-08): no card frame, no decorative glow
          or texture. Content sits on the app background, not in a box; the dark
          continues from the landing rather than floating as a contained island.
          The shell keeps its token overrides so children still render dark —
          only the visible frame is removed. Consistency target is 3.2, not home. ── */
    position: relative;
    margin-bottom: 24px;
    color: var(--color-text-primary);
    padding: 16px 0 24px;
    border: none;
    border-radius: 0;
    background: transparent;
    container-type: inline-size;
  }
  /* texture removed with the frame (full-bleed) — no decorative dot grid */
  .ds-shell::before { content: none; }
  .ds-shell > * { position: relative; z-index: 1; }
  .ds-shell option { background: var(--ds-option-bg); color: ${C.textHi}; }

  /* ── MetricMap "flair" hero panel inside a DsShell ─────────────────────────
     The flair panel (.mmap-panel) paints an opaque navy gradient inline — a
     fixed dark island that does NOT flip with the theme. Its inner text/borders
     read var(--color-*), so on the LIGHT theme (where the shell flips those
     tokens dark) the panel's numbers would render dark-on-navy and vanish.
     Re-pin the dark-shell text/border/surface tokens on the panel so it stays
     legible in both themes — the same "dark island in a light app" contract the
     shell itself uses. (IntelShell dodges this by being dark-only; DsShell
     themes, so it must pin here.) ── */
  .ds-shell .mmap-panel {
    --color-text-primary: #e9eefc;
    --color-text-secondary: #8d98b6;
    --color-text-muted: #5d6883;
    --color-border: rgba(255,255,255,0.12);
    --color-surface: rgba(255,255,255,0.05);
    --color-tooltip-bg: #141a26;
    color-scheme: dark;
  }

  /* ── slide anatomy: kicker row (kicker ↔ controls) → display H2 (+ italic
        accent word) → lede. Laid out as rows, not side-by-side columns — at
        the tree view's column widths a side-by-side head forces the title to
        wrap and blows the one-screen budget. Title sizes with the container
        (cqw) so it holds a single line at every column this shell lives in. ── */
  .ds-head {
    position: relative;
    z-index: 5;
    margin-bottom: 18px;
  }
  .ds-kick-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .ds-kick {
    font-family: ${FM};
    font-size: ${TYPE.eyebrow.fontSize}px;
    font-weight: ${TYPE.eyebrow.fontWeight};
    letter-spacing: ${TYPE.eyebrow.letterSpacing};
    text-transform: uppercase;
    color: var(--ds-kick);
  }
  .ds-title {
    margin: 0;
    font-family: ${FS};
    font-size: ${TYPE.hero.fontSize};
    font-weight: ${W.light};
    letter-spacing: 0;
    line-height: ${TYPE.hero.lineHeight};
    color: ${C.textHi};
  }
  .ds-title em { font-style: italic; color: var(--ds-accent-ink); }
  .ds-lede {
    max-width: 110ch;
    margin: 8px 0 0;
    font-family: ${FU};
    font-size: ${T.body}px;
    line-height: 1.5;
    color: ${C.textMd};
  }
  .ds-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .ds-field { display: flex; flex-direction: row; align-items: center; gap: 8px; }
  .ds-field-label {
    font-family: ${FU};
    font-size: ${T.tag}px;
    font-weight: ${W.semibold};
    color: ${C.textLo};
    text-transform: uppercase;
    letter-spacing: 0.18em;
  }

  /* ── step rail (A6 #10, unified states) — single-line pills; the two-line
        variant costs ~20px of the one-screen budget for no added meaning ── */
  .ds-rail {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .ds-pill {
    min-width: 0;
    min-height: 40px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 9px;
    text-align: left;
    border: 1px solid ${C.border};
    border-radius: 10px;
    padding: 8px 12px;
    background: ${C.surface};
    cursor: pointer;
    transition: border-color 200ms ease, background 200ms ease;
  }
  .ds-pill .pn { font-family: ${FM}; font-size: ${TYPE.label.fontSize}px; font-weight: ${TYPE.label.fontWeight}; letter-spacing: ${TYPE.label.letterSpacing}; text-transform: uppercase; color: ${C.textLo}; white-space: nowrap; }
  .ds-pill .pt { font-family: ${FU}; font-weight: ${W.semibold}; font-size: ${T.cap}px; color: ${C.textMd}; line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ds-pill:hover:not(.active) { border-color: ${C.borderStrong}; }
  .ds-pill.done { border-color: ${C.borderStrong}; }
  .ds-pill.done .pt { color: ${C.textHi}; }
  /* Active state takes the WORKSPACE identity hue (--ds-accent, set inline per
     section) — not the legacy engine-green (--color-accent inside the shell) —
     so a prioritization control reads purple, a GTM control amber, etc. */
  .ds-pill.active { border-color: color-mix(in srgb, var(--ds-accent, var(--intel-accent, var(--src-accent, var(--color-accent)))) 45%, transparent); background: color-mix(in srgb, var(--ds-accent, var(--intel-accent, var(--src-accent, var(--color-accent)))) 12%, transparent); }
  .ds-pill.active .pt { color: ${C.textHi}; }
  .ds-pill.active .pn { color: var(--ds-accent-ink, var(--intel-accent, var(--src-accent, var(--color-accent)))); }

  /* ── glass panel (A6 #15) ── */
  .ds-panel {
    border: 1px solid ${C.border};
    border-radius: 16px;
    background: ${C.surface};
    backdrop-filter: blur(8px);
    padding: 16px 18px;
  }
  .ds-panel-h { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
  .ds-panel-h .ph-t { font-family: ${FS}; font-weight: ${W.light}; font-size: ${T.head}px; color: ${C.textHi}; }
  .ds-panel-h .ph-s { font-family: ${FU}; font-size: ${T.tag}px; color: ${C.textMd}; letter-spacing: 0.14em; text-transform: uppercase; }

  /* ── chips: filters / segmented toggles (A6 #17) ── */
  .ds-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    font-family: ${FU};
    font-size: ${T.micro}px;
    font-weight: ${W.semibold};
    letter-spacing: 0.04em;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid ${C.border};
    background: var(--ds-wash);
    color: ${C.textMd};
    cursor: pointer;
    white-space: nowrap;
    transition: color 160ms ease, border-color 160ms ease, background 160ms ease;
  }
  .ds-chip:hover { color: ${C.textHi}; border-color: ${C.borderStrong}; }
  .ds-chip.active {
    color: var(--ds-accent-ink, var(--intel-accent, var(--src-accent, var(--color-accent))));
    background: color-mix(in srgb, var(--ds-accent, var(--intel-accent, var(--src-accent, var(--color-accent)))) 12%, transparent);
    border-color: color-mix(in srgb, var(--ds-accent, var(--intel-accent, var(--src-accent, var(--color-accent)))) 40%, transparent);
  }
  .ds-chip-row { display: flex; gap: 6px; flex-wrap: wrap; }

  /* ── heat strip / heat grid (A6 #18) — sized so the full ~60-market
        population lands in 3 rows at every column the shell lives in ── */
  .ds-heat {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
    gap: 4px;
  }
  .ds-heat-cell {
    position: relative;
    min-height: 30px;
    min-width: 0;
    overflow: hidden;
    border: 1px solid transparent;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    padding: 2px 1px;
    cursor: pointer;
    transition: transform 150ms ease, border-color 150ms ease;
  }
  .ds-heat-cell:hover { transform: scale(1.12); z-index: 2; }
  .ds-heat-cell.sel { border-color: var(--ds-sel-ring); }
  .ds-heat-cell .hc-iso { font-family: ${FU}; font-size: 9px; font-weight: ${W.semibold}; letter-spacing: 0.04em; line-height: 1; }
  .ds-heat-cell .hc-v { font-family: ${FS}; font-size: ${T.tag}px; line-height: 1.1; font-variant-numeric: tabular-nums; }
  .ds-heat-cell.top10::after {
    content: "";
    position: absolute;
    top: 0;
    left: 6px;
    right: 6px;
    height: 2px;
    border-radius: 2px;
    background: var(--color-amber-text);
    opacity: 0.85;
  }
  .ds-legend {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-family: ${FU};
    font-size: ${T.tag}px;
    color: ${C.textMd};
    margin-top: 6px;
  }
  .ds-legend .lg-bar { width: 96px; height: 7px; border-radius: 4px; }
  .ds-legend .lg-swatch { display: inline-block; width: 11px; height: 11px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }

  /* ── buttons ── */
  .ds-ghost {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 32px;
    font-family: ${FU};
    font-size: ${T.cap}px;
    font-weight: ${W.semibold};
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid ${C.border};
    background: ${C.surface};
    color: ${C.textMd};
    cursor: pointer;
    transition: color 160ms ease, border-color 160ms ease;
  }
  .ds-ghost:hover { color: ${C.textHi}; border-color: ${C.borderStrong}; }
  .ds-cta {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-family: ${FU};
    font-size: ${T.body}px;
    font-weight: ${W.semibold};
    color: ${C.onAccent};
    background: ${C.accent};
    border: none;
    border-radius: 8px;
    padding: 11px 18px;
    cursor: pointer;
    box-shadow: var(--ds-engine-glow);
    transition: background 160ms ease, transform 160ms ease;
  }
  .ds-cta:hover:not(:disabled) { background: ${C.accentHover}; transform: translateY(-1px); }

  /* ── provenance footer (A6 #20) ── */
  .ds-provenance {
    font-family: ${FU};
    font-size: ${T.micro}px;
    line-height: 1.5;
    color: ${C.textMd};
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid ${C.border};
  }
  .ds-provenance b { color: ${C.textHi}; font-weight: ${W.semibold}; }
  .ds-provenance .ds-stamp { font-family: ${FM}; font-size: ${T.tag}px; color: ${C.textLo}; letter-spacing: 0.04em; }

  /* ── engine-idle / error / gated states (spec gap #2) ── */
  .ds-idle {
    padding: 48px 22px;
    text-align: center;
    border: 1px solid ${C.border};
    border-radius: 16px;
    background: ${C.surface};
    backdrop-filter: blur(8px);
  }
  .ds-idle-kick {
    font-family: ${FU};
    font-size: ${T.cap}px;
    font-weight: ${W.semibold};
    color: ${C.textMd};
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }
  .ds-idle-title { margin-top: 10px; font-family: ${FS}; font-size: ${T.title}px; font-weight: ${W.light}; color: ${C.textLo}; }
  .ds-idle-body { margin: 10px auto 0; max-width: 640px; font-family: ${FU}; font-size: ${T.body}px; line-height: 1.55; color: ${C.textMd}; }

  /* ── skeleton (spec gap #1) — pulse only when motion is allowed ── */
  .ds-skeleton {
    border-radius: 16px;
    border: 1px solid var(--ds-hair);
    background: ${C.surface};
  }
  @media (prefers-reduced-motion: no-preference) {
    .ds-skeleton { animation: dsPulse 2.4s ease-in-out infinite; }
  }
  @keyframes dsPulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

  /* ── step-panel entrance (A5) — reduced-motion ships static ── */
  @media (prefers-reduced-motion: no-preference) {
    .ds-step-panel { animation: dsStepIn 360ms cubic-bezier(.22,.61,.36,1) both; }
  }
  @keyframes dsStepIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

  /* ── audit layer: a MODAL dialog — a dimmed, blurred backdrop over the whole
        design with a centred, scrollable diagnostics panel. (Was an in-place
        absolute overlay; promoted to a true pop-up per design feedback.) ── */
  .ds-audit {
    position: fixed;
    inset: 0;
    z-index: 200;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 48px 24px;
    overflow-y: auto;
    overscroll-behavior: contain;
    background: color-mix(in srgb, #040814 68%, transparent);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    animation: dsAuditFade 180ms ease;
  }
  @keyframes dsAuditFade { from { opacity: 0; } to { opacity: 1; } }
  /* Full-bleed click-away scrim (a real button for a11y), behind the panel. */
  .ds-audit-scrim {
    position: fixed;
    inset: 0;
    z-index: 0;
    border: none;
    padding: 0;
    margin: 0;
    background: transparent;
    cursor: default;
  }
  .ds-audit-modal {
    position: relative;
    z-index: 1;
    width: min(1060px, 100%);
    margin: auto 0;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 96px);
    border: 1px solid ${C.borderStrong};
    /* Workspace-hued top accent (purple in Prioritization) — the identity signature. */
    border-top: 2px solid var(--ds-accent);
    border-radius: 16px;
    background: var(--ds-scrim);
    backdrop-filter: blur(10px);
    box-shadow: 0 40px 120px -30px rgba(0,0,0,0.7), var(--shadow-card-panel);
    animation: dsAuditRise 200ms cubic-bezier(.2,.7,.2,1);
  }
  @keyframes dsAuditRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  .ds-audit-modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid ${C.border};
  }
  .ds-audit-modal-body {
    padding: 16px 20px 22px;
    overflow-y: auto;
  }
  /* Icon-only close — the product pattern is a bare ✕, never a "Close" label. */
  .ds-audit-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    width: 30px;
    flex-shrink: 0;
    border: 1px solid ${C.border};
    border-radius: 8px;
    background: transparent;
    color: ${C.textMd};
    cursor: pointer;
    transition: color 140ms ease, border-color 140ms ease, background 140ms ease;
  }
  .ds-audit-close:hover {
    color: ${C.textHi};
    border-color: ${C.borderStrong};
    background: color-mix(in srgb, var(--ds-accent) 10%, transparent);
  }

  /* ── focus visibility (spec gap #3) ── */
  .ds-shell :focus-visible {
    outline: 1px solid var(--color-accent);
    outline-offset: 1px;
    box-shadow: 0 0 0 3px var(--ds-engine-dim);
  }

  /* ── export appendix: mounted, screen-hidden, print-visible (P1/§D4) ── */
  .ds-print-appendix { display: none; }

  /* ── present mode: the deck stage owns kicker/thesis/pager and adds its own
        chrome above and below the slide, so the shell gives back its deep
        bottom padding to stay inside one stage height ── */
  .present .ds-shell { padding-bottom: 26px; }

  /* ── short-viewport density step (the 1280×800 floor): same anatomy, the
        chrome gives back ~60px so the content zone keeps its share ── */
  @media (max-height: 860px) {
    .ds-shell { padding: 20px clamp(18px, 3vw, 28px) 20px; }
    .ds-title { font-size: clamp(24px, 2.9cqw, 30px); }
    .ds-head { margin-bottom: 10px; }
    .ds-kick-row { margin-bottom: 8px; }
    .ds-lede { margin-top: 6px; }
    .ds-rail { margin-bottom: 8px; }
    .ds-pill { min-height: 36px; padding: 6px 12px; }
    .ds-chip { padding: 3px 9px; }
    .ds-heat-cell { min-height: 24px; }
    .ds-heat-cell .hc-v { font-size: 9.5px; }
    .ds-legend { margin-top: 2px; }
    .ds-provenance { margin-top: 6px; padding-top: 7px; line-height: 1.45; }
    .ds-idle { padding: 32px 22px; }
  }

  /* ── §A0 addition 4: one shared print pattern, keyed off the shell — no
        hardcoded ids, and the palette flips light so nothing prints
        white-on-white with backgrounds stripped ── */
  @media print {
    body:has(.ds-shell[data-ds-print-root]) * { visibility: hidden !important; }
    .ds-shell[data-ds-print-root], .ds-shell[data-ds-print-root] * { visibility: visible !important; }
    .ds-shell[data-ds-print-root] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      margin: 0;
      padding: 0 !important;
      border: none;
      border-radius: 0;
      background: #ffffff !important;
      color-scheme: light;
      --color-bg-primary: #ffffff;
      --color-bg-secondary: #f0f6ff;
      --color-surface: #ffffff;
      --color-surface-alt: #f0f6ff;
      --color-tooltip-bg: #ffffff;
      --color-border: #e0e7ef;
      --color-border-strong: #ceecff;
      --color-text-primary: #000f47;
      --color-text-secondary: #55524e;
      --color-text-muted: #7b7974;
      --color-text-on-accent: #ffffff;
      --color-fill-strong: #000f47;
      --color-on-fill-strong: #ffffff;
      --color-overlay: rgba(0,15,71,0.05);
      --color-overlay-strong: rgba(0,15,71,0.12);
      --color-highlight-soft: #fff3da;
      --color-amber-text: #8a5200;
      --color-map-ocean: #ffffff;
      --color-map-land-empty: #e4e6e3;
      --color-map-no-data: #e4e6e3;
      --mmap-stroke: rgba(0,15,71,0.45); /* navy border for the white print ocean */
    }
    .ds-shell[data-ds-print-root]::before { display: none; }
    .ds-shell[data-ds-print-root] .ds-rail,
    .ds-shell[data-ds-print-root] .ds-controls,
    .ds-shell[data-ds-print-root] .ds-audit { display: none !important; }
    .ds-shell[data-ds-print-root] .ds-panel { box-shadow: none !important; break-inside: avoid; }
    .ds-shell[data-ds-print-root] .ds-print-appendix { display: block !important; break-inside: auto; }
  }
`;
